<template>
    <div class="w-full py-20 border-t border-blue-light text-14">
        <template v-for="metric in metricsWithTargets">
            <target :target="metric.target" :label="metric.label" :can-delete="creating" @delete="onDelete"></target>
        </template>

        <form-wrapper v-if="creating" :validator="$v">
            <h4 class="pb-8 font-bold text-16 pt-14">Add new target</h4>

            <form-group name="selectedMetric" class="mb-14">
                <multiselect
                    :options="chart.metrics"
                    :searchable="false"
                    :show-labels="false"
                    @input="onMetricSelect"
                    label="label"
                    placeholder="Select metric..."
                    track-by="id"
                    v-model="selectedMetric"
                ></multiselect>
            </form-group>

            <form-group name="target.end_at" class="mb-14">
                <multiselect
                    :custom-label="typeLabel"
                    :options="typeOptions"
                    :searchable="false"
                    :show-labels="false"
                    @input="onType"
                    label="end_at"
                    placeholder="Select type or end date..."
                    track-by="end_at"
                    v-model="selectedType"
                ></multiselect>
            </form-group>

            <form-group name="target.start_at" class="mb-14">
                <multiselect
                    :custom-label="startAtLabel"
                    :options="startAtOptions"
                    :searchable="false"
                    :show-labels="false"
                    @input="onSelectStartAt"
                    placeholder="Select start date..."
                    v-model="selectedStartAt"
                ></multiselect>
            </form-group>

            <form-group v-if="target.type !== 'constant'" name="target.start_value" class="mb-14">
                <input
                    slot-scope="{attributes}"
                    v-model="target.start_value"
                    @input="delayTouch($v.target.start_value)"
                    v-bind="attributes"
                    type="text"
                    placeholder="Enter start value..."
                    class="w-full"
                >
            </form-group>

            <form-group name="target.end_value" class="mb-14">
                <input
                    slot-scope="{attributes}"
                    v-model="target.end_value"
                    @input="delayTouch($v.target.end_value)"
                    v-bind="attributes"
                    type="text"
                    placeholder="Enter target value..."
                    class="w-full"
                >
            </form-group>
        </form-wrapper>

        <div v-if="errors" class="flex flex-col p-8 my-14 bg-coral-darkest">
            <span v-for="error in errors" class="mb-2 text-white">
                {{error[0]}}
            </span>
        </div>

        <div class="flex justify-end w-full pt-15 pb-30">
            <button v-if="creating && hasTargets" @click="reset" class="mr-4 bg-blue hover:bg-blue-dark">Cancel</button>
            <button @click="onCreate" class="ml-4">
                <span>Update</span>
            </button>
        </div>
    </div>
</template>

<script>
    import { addMonths, format, parseISO, startOfMonth, subMonths } from "date-fns"
    import { minValue, numeric, required } from "vuelidate/lib/validators"
    import { call, get } from 'vuex-pathify'
    import Multiselect from "vue-multiselect"
    import Target from './target'
    import validationMixin from "../../../../mixins/validation"

    export default {
        model: {
            prop: 'chart',
        },

        props: {
            chart: {
                type: Object,
                required: true
            },
        },

        mixins: [validationMixin],

        components: {
            Multiselect,
            Target,
        },

        data() {
            return {
                creating: false,

                errors: null,

                selectedMetric: null,

                selectedStartAt: null,

                selectedType: null,

                target: {
                    end_at: null,
                    end_value: null,
                    start_at: null,
                    start_value: null,
                    type: null,
                    user: null,
                }
            }
        },

        computed: {
            client: get('auth/client'),
            user: get('auth/user'),

            hasTargets() {
                return this.metricsWithTargets.length > 0
            },

            metricsWithTargets() {
                return this.chart.metrics.filter(metric => metric.target)
            },

            startAtOptions() {
                let months = 12
                let options = []
                let start = subMonths(startOfMonth(new Date()), 1)

                for (let index = 0; index < months; index++) {
                    options.push({start_at: start})

                    start = subMonths(start, 1)
                }

                return options
            },

            typeOptions() {
                let months = 12
                let options = []
                let start = addMonths(startOfMonth(new Date()), 1)

                options.push({end_at: null})

                for (let index = 0; index < months; index++) {
                    options.push({end_at: start})

                    start = addMonths(start, 1)
                }

                return options
            },
        },

        mounted() {
            if (!this.hasTargets) {
                this.creating = true
            }
        },

        methods: {
            create: call('targets/create'),
            destroy: call('targets/destroy'),
            loadChart: call('charts/load'),
            loadChartData: call('charts/loadChartData'),
            update: call('targets/update'),

            async onCreate() {
                if (!this.creating) {
                    this.creating = true

                    return
                }

                this.$v.$touch()

                if (!this.$v.$invalid) {
                    try {
                        this.setStartValue()

                        this.target.end_at = this.target.end_at ? format(this.target.end_at, 'yyyy-MM-dd') : null
                        this.target.start_at = this.target.start_at ? format(this.target.start_at, 'yyyy-MM-dd') : format(subMonths(new Date(), 2), 'yyyy-MM-dd')

                        if (this.target.id) {
                            await this.update(this.target)
                        } else {
                            await this.create({metric: this.selectedMetric, target: this.target})
                        }

                        await this.loadChart(this.chart.id)

                        this.$emit('updated', this.chart)
                        this.$emit('input', this.chart)
                    } catch (error) {
                        if (error.response && error.response.data.errors) {
                            this.errors = error.response.data.errors
                        }
                    }

                    this.reset()
                }
            },

            async onDelete(target) {
                try {
                    await this.destroy(target)
                    await this.loadChart(this.chart.id)

                    this.$emit('updated', this.chart)
                    this.$emit('input', this.chart)
                } catch (error) {
                    if (error.response && error.response.data.errors) {
                        this.errors = error.response.data.errors
                    }
                }
            },

            onMetricSelect(select) {
                if (select.target) {
                    this.target = Object.assign({}, select.target)
                    this.target.end_at = this.target.end_at ? parseISO(this.target.end_at) : this.target.end_at
                    this.target.start_at = parseISO(this.target.start_at)
                    this.selectedStartAt = Object.assign({}, {start_at: this.target.start_at})
                    this.selectedType = Object.assign({}, {end_at: this.target.end_at})
                } else {
                    this.target = {
                        end_at: null,
                        end_value: null,
                        start_at: null,
                        start_value: null,
                        type: null,
                        user: null,
                    }
                    this.selectedStartAt = null
                    this.selectedType = null

                    this.$v.$reset()
                }
            },

            onSelectStartAt(select) {
                this.target.start_at = select.start_at
            },

            onType(select) {
                this.target.end_at = select.end_at
                this.target.type = select.end_at ? 'expotential' : 'constant'
            },

            reset() {
                this.creating = false
                this.target = {
                    end_at: null,
                    end_value: null,
                    start_at: null,
                    start_value: null,
                    type: null,
                    user: null,
                }
                this.selectedMetric = null
                this.selectedStartAt = null
                this.selectedType = null

                this.$v.$reset()
            },

            setStartValue() {
                if (this.target.type === 'constant') {
                    this.target.start_value = this.target.end_value
                } else if (!this.target.start_value) {
                    let thisMonth = new Date()
                    let lastMonth = subMonths(new Date(), 1)
                    let startMonth = this.target.start_at
                    let index = this.chart.dimensions[0].labels.findIndex(label => {
                        if (label === format(startMonth, 'MMM') || label === format(startMonth, 'MMM yy')) {
                            return true
                        }

                        if (label === format(lastMonth, 'MMM') || label === format(lastMonth, 'MMM yy')) {
                            return true
                        }

                        return label === format(thisMonth, 'MMM')
                    })

                    this.target.start_value = this.chart.datasets[0].data[index]
                }

                this.target.end_value = parseFloat(this.target.end_value)
                this.target.start_value = parseFloat(this.target.start_value)
            },

            startAtLabel({start_at}) {
                return format(start_at, "'from' MMMM yyyy")
            },

            typeLabel({end_at}) {
                if (!end_at) {
                    return 'Constant'
                }

                return format(end_at, "'by' MMMM yyyy")
            },
        },

        validations: {
            selectedMetric: {
                required
            },

            target: {
                type: {
                    required,
                },
                end_value: {
                    required,
                    minValue: minValue(0),
                },
                start_value: {
                    minValue: minValue(0),
                },
                start_at: {
                    required,
                },
            }
        }
    }
</script>
